'use client'
import { useInlineMemoizedContentGetter, useMemoizedContentGetter } from 'utils/contentful'
import Section from 'components/Section'
import { LinkButton, Typography } from 'components/Contentful'
import React, { useState } from 'react'
import { TitleDivider } from 'components/TitleDivider'
import { ChevronDown, ChevronUp } from 'react-feather'
import { _get } from 'utils/shared'
import { returnContentStringFaqAnswer } from 'utils/shared'
import { FaqAccordion } from 'components/FaqAccordion'
import classNames from 'classnames'
import { Grid } from '@achieve/ascend'
import { Button } from 'components/Button'
import { FdrPage } from 'utils/environmentConfig'
import logger from '@achieve/cx-logger'
import Script from 'next/script'
import Cookies from 'js-cookie'

function Faqs({ content }) {
  const sectionVariationObject = {
    ...content?.fields?.experiment,
    variant: content?.fields?.experimentVariation,
  }
  const MemoizedContentGetter = content?.fields
    ? useMemoizedContentGetter
    : useInlineMemoizedContentGetter
  const memoizedContent = MemoizedContentGetter(content, [
    'button',
    'faqLink',
    'faqs',
    'title',
    'show',
    'config',
    'accordionEvent',
    'allEvent',
    'backgroundConfig',
  ])
  const sectionName = content?.fields?.fieldName || 'faqs'
  const { button, faqLink, faqs, title, show, config, accordionEvent, allEvent, backgroundConfig } =
    memoizedContent
  const additionalConfiguration = content?.fields?.additionalConfiguration?.jsonContent
  const [filter, setFilter] = useState(0)
  const showMoreText = additionalConfiguration?.showText?.[filter]?.more || 'Show more'
  const showLessText = additionalConfiguration?.showText?.[filter]?.less || 'Show less'
  const faqSets = (faqs?.faqSetsCollection?.items ?? faqs)
    .filter(
      (faqSet) =>
        additionalConfiguration == null ||
        faqSet?.config?.filter == additionalConfiguration?.filters?.[filter]
    )
    .map((faqSet) => {
      const set = {
        ...faqSet,
        ...faqSet?.fields,
      }
      const answer = set?.answer?.json ?? set?.answer
      const question = set?.question?.json ?? set?.question

      return {
        ...set,
        answer,
        question,
      }
    })

  return (
    <FaqsSection
      button={button}
      faqLink={faqLink}
      faqs={faqs}
      title={title}
      show={show}
      config={config}
      accordionEvent={accordionEvent}
      allEvent={allEvent}
      sectionName={sectionName}
      faqSets={faqSets}
      sectionVariationObject={sectionVariationObject}
      additionalConfiguration={additionalConfiguration || {}}
      setFilter={setFilter}
      filter={filter}
      showMoreText={showMoreText}
      showLessText={showLessText}
      backgroundConfig={backgroundConfig}
    />
  )
}

function FaqsSection({
  button,
  faqLink,
  faqs,
  title,
  show,
  config,
  accordionEvent,
  allEvent,
  questionTextVariant,
  answerTextVariant,
  titleProps,
  fullWidth = false,
  sectionName,
  faqSets,
  sectionVariationObject,
  additionalConfiguration,
  filter,
  setFilter,
  showMoreText,
  showLessText,
  backgroundConfig,
}) {
  const siteEnv = process.env.NEXT_PUBLIC_SITE_ENVIRONMENT || 'achieve'
  const leadId = Cookies.get('leadId') || ''
  const [selected, setSelected] = useState('set1')
  const [open, setOpen] = useState(false)
  const showByDefault =
    typeof show === 'number'
      ? show
      : _get(show, 'textContent.content[0].content[0].value') ||
        _get(show, 'textContent.json.content[0].content[0].value')

  const showDefault = Number(showByDefault) >= 0 ? Number(showByDefault) : 5
  const encryptionKey = faqs?.encryptionKey

  const makeAccordion = (
    articles,
    formConfig,
    miniCalculatorConfig,
    config,
    answer,
    question,
    setNumber,
    open,
    index,
    faqId
  ) => {
    return (
      <React.Fragment key={faqId}>
        <FaqAccordion
          formConfig={formConfig}
          articles={articles}
          answer={answer}
          question={question}
          setNumber={setNumber}
          selected={selected}
          setSelected={setSelected}
          open={open}
          index={index}
          faqId={faqId}
          questionTextVariant={questionTextVariant}
          answerTextVariant={answerTextVariant}
          encryptionKey={encryptionKey}
          miniCalculatorConfig={miniCalculatorConfig}
        />
      </React.Fragment>
    )
  }

  const makeSeoEntity = (answerData, questionData) => {
    const answer = returnContentStringFaqAnswer(answerData)
    const question = returnContentStringFaqAnswer(questionData)
    return {
      questionName: question,
      acceptedAnswerText: answer,
    }
  }

  const mapFaqSets = () => {
    let entities = []
    let faqs = []

    faqSets.map((set, index) => {
      const answer = set?.answer
      const question = set?.question
      const setNumber = set?.fieldName
      const articles = set?.articlesCollection?.items || []
      const formConfig = set?.formConfig || false
      const miniCalculatorConfig = set?.miniCalculatorConfig || false
      if (!question) {
        logger.error(`Skipping FAQ entry due to missing question. Question: "${question}"`)
        return null
      }
      const config = set?.config || false
      const entity = makeSeoEntity(answer, question)
      const accordion = makeAccordion(
        articles,
        formConfig,
        miniCalculatorConfig,
        config,
        answer,
        question,
        setNumber,
        open || index + 1 <= showDefault,
        index,
        set?.sys?.id
      )
      entities.push(entity)
      faqs.push(accordion)
    })

    return <>{faqs}</>
  }

  return (
    <Section
      fullBackground={backgroundConfig?.jsonContent?.fullBackground ?? false}
      className={classNames(
        config?.jsonContent?.extraClasses ?? '',
        'py-8 px-0 lg:py-12',
        config?.jsonContent?.color & (config?.jsonContent?.color === 'gray')
          ? 'bg-[#f8f9fc]'
          : config?.jsonContent?.color === 'highlight-blue'
          ? 'bg-[#eff5ff]'
          : ''
      )}
      data-testid={`${sectionName}-section`}
      id="faq-section"
    >
      <Grid
        alignItems="center"
        columnSpacing={{ xs: 1, lg: 3 }}
        container
        data-testid="faqs-grid-container"
        justifyContent="center"
      >
        <Grid data-testid="faqs-grid-item-title" size={{ xs: fullWidth ? 12 : 9 }}>
          <TitleDivider
            title={title}
            data-testid="faqs-section-title-area"
            className={
              'text-left mb-8 data-[page-main-variation="website-tenant-1"]:[&_div]:text-[#008000]'
            }
            disableTitleTag={true}
            classNameTitleText="displayMd lg:displayLg font-medium!"
            classNameSubtitleText="bodySm lg:displaySm font-light!"
            {...titleProps}
          />
        </Grid>
        <Grid data-testid="faqs-grid-item-accordion" size={{ xs: 11, lg: fullWidth ? 12 : 9 }}>
          {faqSets && (
            <>
              <Script
                id="faq-schema"
                type="application/ld+json"
                strategy="afterInteractive"
                dangerouslySetInnerHTML={{
                  __html: JSON.stringify({
                    '@context': 'https://schema.org',
                    '@type': 'FAQPage',
                    mainEntity: faqSets.map((set) => {
                      const answer = returnContentStringFaqAnswer(set?.answer) ?? ''
                      const question = returnContentStringFaqAnswer(set?.question) ?? ''
                      if (!question || !answer) {
                        logger.error(
                          `Skipping FAQ Schema due to missing question or answer. Question: "${question}", Answer: "${answer}"`
                        )
                        return null
                      }
                      return {
                        '@type': 'Question',
                        name: question,
                        acceptedAnswer: {
                          '@type': 'Answer',
                          text: answer,
                        },
                      }
                    }),
                  }),
                }}
              />
              {additionalConfiguration?.filters?.length && (
                <div className="mt-2 flex items-center justify-center pb-6">
                  <div className="p-1 rounded-md bg-[#D2E2FE]" data-testid="faqs-filter-container">
                    {additionalConfiguration?.filters?.map((item, index) => {
                      return (
                        <Button
                          key={`filter-${index}`}
                          onClick={() => setFilter(index)}
                          className={`${
                            filter === index ? 'bg-white ' : ''
                          } text-[#42546B] min-w-[150px] `}
                          size="small"
                        >
                          <Typography
                            content={item}
                            variant="labelSm"
                            fontWeight={filter === index ? 'bold' : 'regular'}
                            className="text-center"
                          />
                        </Button>
                      )
                    })}
                  </div>
                </div>
              )}
              {mapFaqSets()}
            </>
          )}
        </Grid>
        {faqSets?.length > showDefault && (
          <Grid data-testid="faqs-grid-item-button" size={{ xs: 11 }} sx={{ pt: 8 }}>
            <Grid container justifyContent="center">
              <Button
                variant="text"
                onClick={() => setOpen(!open)}
                endIcon={open ? <ChevronUp /> : <ChevronDown />}
                track={{
                  ...{
                    list_name: 'FAQ ACCORDION LESS/MORE',
                    click_id: open ? 'Less' : 'More',
                    click_text: open ? showLessText : showMoreText,
                    click_type: 'UI Click',
                    nav_link_section: 'FAQs',
                    click_position: '0',
                    track_event: 'ui_click',
                    event_action: 'ui_click',
                    event_type: 'ui_click',
                    sectionVariationObject,
                  },
                  ...accordionEvent?.jsonContent,
                }}
              >
                {open ? showLessText : showMoreText}
              </Button>
            </Grid>
          </Grid>
        )}
        {faqLink && (
          <Grid
            data-testid="faqs-grid-item-link-faq"
            size={{ xs: 11 }}
            sx={{ pt: 8 }}
            className={'pt-8'}
          >
            <Grid container justifyContent="center">
              <LinkButton
                className={'h-[47px] w-[293px]'}
                content={faqLink}
                data-testid="faqs-link-faq"
                variant="text"
                typographicOptions={{
                  fontWeight: 'bold',
                  'data-testid': 'faqs-link-faq-text',
                }}
                track={{
                  ...{
                    list_name: 'FAQ SEE ALL',
                    click_id: 'See all FAQs',
                    click_text: 'See all FAQs',
                    nav_link_section: 'FAQs',
                    track_event: 'page_nav_click',
                    event_action: 'link_click',
                    event_type: 'link_click',
                    sectionVariationObject,
                  },
                  ...allEvent?.jsonContent,
                }}
              />
            </Grid>
          </Grid>
        )}

        {button && (
          <Grid
            data-testid="faqs-grid-item-button"
            size={{ xs: 11 }}
            sx={{ pt: 8 }}
            className={'pt-8'}
          >
            <Grid container justifyContent="center">
              <LinkButton
                track={{
                  ...{
                    list_name: 'Product Page FAQ CTA',
                    click_id: button?.linkText?.json?.content[0]?.content[0].value,
                    click_url: button?.linkHref.replace('{leadId}', leadId),
                    click_type: 'Button Click',
                    click_position: '0',
                    nav_link_section: 'FAQs',
                    track_event: 'internal_campaign_click',
                    event_action: 'button_click',
                    event_type: 'button_click',
                    sectionVariationObject,
                  },
                  ...button?.event,
                }}
                className={classNames('h-[47px] w-[293px]', {
                  'bg-[#cb000e] hover:bg-[#7A0D0D] hover:text-[white] leading-[1.2] w-full px-4 lg:w-auto lg:px-6':
                    siteEnv === FdrPage,
                })}
                content={{
                  ...button,
                  linkHref: button?.linkHref.replace('{leadId}', leadId),
                }}
                data-testid="faqs-button"
                typographicOptions={{
                  fontWeight: 'bold',
                  'data-testid': 'faqs-button-text',
                }}
              />
            </Grid>
          </Grid>
        )}
      </Grid>
    </Section>
  )
}
export { Faqs }
export default Faqs
